import { RoutingPolicyPortalRuleFragment } from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import * as Icon from '../components/icons';
import { PolicyFilter } from '../components/policies/PolicyFilter';
import PolicyFilterItem from '../components/policies/PolicyFilterItem';
import { viewerShowPortals } from '../layout/viewer/store/viewer.state';
import { routingPolicySelectedIdentity } from '../simulation/store/routingPolicy/routingPolicy.state';
import { simulationIsEditable } from '../simulation/store/simulation.state';
import { sidebarStateByType } from '../store/sidebar.state';

export type RoutingPolicyPortalsRuleProps = {
  ruleId: string;
  portalRules: RoutingPolicyPortalRuleFragment[];
};

const RoutingPolicyPortalsRule: React.FC<
  RoutingPolicyPortalsRuleProps
> = props => {
  const { ruleId } = props;
  const { t } = useTranslation('simulation');
  const [selectedIdentity, setSelectedIdentity] = useRecoilState(
    routingPolicySelectedIdentity,
  );
  const [policyEditPanelState, setPolicyEditPanelState] = useRecoilState(
    sidebarStateByType('sidebar-routing-policy-editor'),
  );
  const canUpdate = useRecoilValue(simulationIsEditable);
  const setShowPortals = useSetRecoilState(viewerShowPortals);

  const isActive =
    selectedIdentity?.ruleId === ruleId &&
    (!canUpdate || selectedIdentity?.isRoutingPortalsSelected);

  function toggleSelect() {
    const shouldSelect = !isActive;
    const newIdentity = shouldSelect
      ? {
          ruleId: ruleId,
          isRoutingAgentSelected: false,
          isRoutingPortalsSelected: canUpdate,
        }
      : null;

    setSelectedIdentity(newIdentity);
    setShowPortals(shouldSelect);

    if (canUpdate) {
      setPolicyEditPanelState({
        ...policyEditPanelState,
        isCollapsed: !policyEditPanelState.isPinned
          ? !shouldSelect
          : policyEditPanelState.isCollapsed,
        isHidden: !shouldSelect,
      });
    }
  }

  const disabledPortals = _.filter(props.portalRules, r => r.blocked);
  const restrictedFeaturesCount = _.size(disabledPortals);
  const zeroRestrictions = restrictedFeaturesCount === 0;

  return (
    <PolicyFilter
      data-component="RoutingPolicyPortalsRule"
      className="p-1 xl:p-2"
      key={`PolicyFilter-for-features-${ruleId}`}
      label={t`Portals Restrictions`}
      isActive={isActive}
      onClick={toggleSelect}
    >
      <PolicyFilterItem
        key={`PolicyFilterItem-for-features-overall`}
        isDisabled={!canUpdate}
        isActive={!canUpdate ? false : isActive}
        value={
          zeroRestrictions
            ? t('No Restrictions')
            : t('{{restrictedFeaturesCount}} disabled portals', {
                restrictedFeaturesCount,
              })
        }
        icon={zeroRestrictions ? Icon.CircleWaypoint4 : Icon.CircleArrowTwoWay}
        iconClassName={classNames(
          'w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6',
          'ltr:mr-2 rtl:ml-2',
        )}
      />
    </PolicyFilter>
  );
};

export default RoutingPolicyPortalsRule;
